import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Right-side product variant options drawer (dialog).
 */
class ProductVariantDrawerComponent extends DialogComponent {
  /** @type {HTMLDialogElement | null} */
  #zoomDialog = null;

  /** @type {HTMLImageElement | null} */
  #zoomImg = null;

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onAddClick);
    this.addEventListener('click', this.#onZoomOpenClick);
    this.#zoomDialog = this.querySelector('.product-variant-drawer__zoom-dialog');
    this.#zoomImg = this.querySelector('.product-variant-drawer__zoom-img');
    this.#zoomDialog?.addEventListener('click', this.#onZoomDialogPointer);
    this.#zoomDialog?.addEventListener('click', this.#stopZoomEventBubbling);
    this.#zoomDialog?.addEventListener('keydown', this.#stopZoomEscapeBubbling);
    this.#zoomDialog?.addEventListener('close', this.#onZoomDialogClose);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onAddClick);
    this.removeEventListener('click', this.#onZoomOpenClick);
    this.#zoomDialog?.removeEventListener('click', this.#onZoomDialogPointer);
    this.#zoomDialog?.removeEventListener('click', this.#stopZoomEventBubbling);
    this.#zoomDialog?.removeEventListener('keydown', this.#stopZoomEscapeBubbling);
    this.#zoomDialog?.removeEventListener('close', this.#onZoomDialogClose);
    super.disconnectedCallback();
  }

  /**
   * Clicks inside the zoom dialog are outside the variant drawer <dialog> ref, so
   * DialogComponent would treat them as "outside" and close the drawer. Stop bubbling
   * so closing zoom only closes the zoom layer.
   *
   * @param {Event} event
   */
  #stopZoomEventBubbling = (event) => {
    event.stopPropagation();
  };

  /**
   * Same for Escape: avoid closing the variant drawer when dismissing the zoom layer.
   *
   * @param {KeyboardEvent} event
   */
  #stopZoomEscapeBubbling = (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
  };

  /**
   * @param {MouseEvent} event
   */
  #onZoomDialogPointer = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-variant-drawer-zoom-close]')) {
      event.preventDefault();
      this.#closeZoom();
      return;
    }
    if (event.target === this.#zoomDialog) {
      this.#closeZoom();
    }
  };

  #closeZoom = () => {
    this.#zoomDialog?.close();
  };

  #onZoomDialogClose = () => {
    if (this.#zoomImg) {
      this.#zoomImg.removeAttribute('srcset');
      this.#zoomImg.src = '';
      this.#zoomImg.alt = '';
    }
  };

  /**
   * @param {MouseEvent} event
   */
  /**
   * @param {MouseEvent} event
   */
  #onZoomOpenClick = (event) => {
    const trigger = event.target.closest('[data-variant-drawer-zoom]');
    if (!trigger || !this.#zoomDialog || !this.#zoomImg) return;

    event.preventDefault();
    const src = trigger.dataset.zoomSrc;
    if (!src) return;

    this.#zoomImg.alt = trigger.dataset.zoomAlt || '';
    const w = parseInt(trigger.dataset.zoomWidth || '0', 10);
    const h = parseInt(trigger.dataset.zoomHeight || '0', 10);
    if (w > 0) this.#zoomImg.width = w;
    if (h > 0) this.#zoomImg.height = h;

    this.#zoomImg.srcset = trigger.dataset.zoomSrcset || '';
    this.#zoomImg.src = src;
    this.#zoomDialog.showModal();
  };

  /**
   * @param {Event} event
   */
  #onAddClick = async (event) => {
    const button = event.target.closest('[data-variant-drawer-add]');
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const qid = button.dataset.quantityInputId;
    const qtyInput = qid ? /** @type {HTMLInputElement | null} */ (this.querySelector(`[id="${qid}"]`)) : null;
    const quantity = Math.max(1, parseInt(qtyInput?.value || button.dataset.quantityDefault || '1', 10) || 1);

    event.preventDefault();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.append('form_type', 'product');
    formData.append('utf8', '✓');
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));

    const stockMessage = button.dataset.stockMessage?.trim();
    if (stockMessage) {
      formData.append('properties[Stock Message]', stockMessage);
    }
    if (button.dataset.preorder === 'true') {
      formData.append('properties[_pre-order]', 'true');
    }

    /** @type {string[]} */
    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.dataset.sectionId;
      if (id) sectionIds.push(id);
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const data = await response.json();

      if (data.status) {
        document.dispatchEvent(
          new CartAddEvent({}, 'product-variant-drawer', {
            didError: true,
            source: 'product-variant-drawer',
            sections: data.sections,
          })
        );
        return;
      }

      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-variant-drawer',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      this.closeDialog();
    } catch (e) {
      console.error(e);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('product-variant-drawer-component')) {
  customElements.define('product-variant-drawer-component', ProductVariantDrawerComponent);
}
